import * as React from 'react';
import * as R from 'ramda';

import { withIntl, IntlProps } from 'ha/i18n';
import * as SearchFilters from 'ha/types/SearchFilters';

import FilterPopover, { WidthType, ButtonElementType } from './FilterPopover';
import TypeFilter from './TypeFilter';
import FurnitureFilter from './FurnitureFilter';
import BillsFilter from './BillsFilter';
import RoomsFilter from './RoomsFilter';
import RegistrationFilter from './RegistrationFilter';
import RecentlyAddedFilter from './RecentlyAddedFilter';
import RulesFilter from './RulesFilter';
import GenderFilter from './GenderFilter';
import SuitableForFilter from './SuitableForFilter';

import { RegistrationCountryCodes } from 'ha/constants/Filters';

export interface Props {
  searchParams: {};
  onChangeFilters: SearchFilters.FilterSubmit;
  loadPreviewNumberOfListings: (searchParams: {}) => void;
  clearPreviewNumberOfListings: () => void;
  typeFilter: SearchFilters.TypeFilterValue;
  furnitureFilter: SearchFilters.FurnitureFilterValue;
  billsFilter: SearchFilters.BillsFilterValue;
  roomsFilter: SearchFilters.RoomsFilterValue;
  registrationFilter: SearchFilters.RegistrationFilterValue;
  recentlyAddedFilter: SearchFilters.RecentlyAddedFilterValue;
  rulesFilter: SearchFilters.RulesFilterValue;
  genderFilter: SearchFilters.GenderFilterValue;
  suitableForFilter: SearchFilters.SuitableForFilterValue;
  countryCode: string;
  button: ButtonElementType;
  toggleFilterPopover?: (open: boolean) => void;
  updateButtonText: string;
}

interface State {
  types: SearchFilters.TypeFilterValue;
  furniture: SearchFilters.FurnitureFilterValue;
  bills: SearchFilters.BillsFilterValue;
  rooms: SearchFilters.RoomsFilterValue;
  registration: SearchFilters.RegistrationFilterValue;
  recentlyAdded: SearchFilters.RecentlyAddedFilterValue;
  rules: SearchFilters.RulesFilterValue;
  gender: SearchFilters.GenderFilterValue;
  suitableFor: SearchFilters.SuitableForFilterValue;
  disabledFilters: string[];
}

type FilterValues = Partial<State>;

const defaultFilters = {
  types: [],
  rules: [],
  gender: [],
  bills: null,
  furniture: null,
  registration: null,
  recentlyAdded: null,
  rooms: { bedroomCount: 0 },
  suitableFor: [],
};

class OtherFilterDesktop extends React.Component<Props & IntlProps, State> {
  state: State;

  constructor(props: Props & IntlProps) {
    super(props);

    this.state = this.getStateFromProps();
  }

  getStateFromProps = () => {
    const types = this.props.typeFilter.filter(Boolean);

    const disabledFilters = [];

    if (
      types.length > 0 &&
      !types.includes(SearchFilters.TypeValue.APARTMENT)
    ) {
      disabledFilters.push('rooms');
    }

    return {
      types,
      furniture: this.props.furnitureFilter,
      bills: this.props.billsFilter,
      rooms: this.props.roomsFilter,
      registration: this.props.registrationFilter,
      recentlyAdded: this.props.recentlyAddedFilter,
      rules: this.props.rulesFilter.filter(Boolean),
      gender: this.props.genderFilter,
      suitableFor: this.props.suitableForFilter,
      disabledFilters,
    };
  };

  handleOpen = () => {
    this.setState(this.getStateFromProps());
  };

  handleClose = () => {
    this.props.clearPreviewNumberOfListings();
  };

  handleUpdate = () => {
    const { onChangeFilters, intl } = this.props;

    const filteredState = R.omit(
      ['disabledFilters', ...this.state.disabledFilters],
      this.state,
    );

    onChangeFilters({ ...defaultFilters, ...filteredState, intl });
  };

  updateFilters = (values: FilterValues) => {
    this.setState(
      state => ({ ...state, ...values }),
      () => {
        const filteredState = R.omit(
          ['disabledFilters', ...this.state.disabledFilters],
          this.state,
        );

        const filteredSearchParams = {
          ...this.props.searchParams,
          ...filteredState,
          categories: this.state.types,
        };

        this.props.loadPreviewNumberOfListings(filteredSearchParams);
      },
    );
  };

  handleReset = () => {
    this.setState({ ...defaultFilters, disabledFilters: [] });
  };

  handleTypeFilterToggle = (
    type: SearchFilters.TypeValue,
    selected: boolean,
  ) => {
    const types = selected
      ? this.state.types.concat(type)
      : this.state.types.filter(current => type !== current);

    const disabledFilters =
      types.length > 0 && !types.includes(SearchFilters.TypeValue.APARTMENT)
        ? this.state.disabledFilters.concat('rooms')
        : this.state.disabledFilters.filter(f => f !== 'rooms');

    this.setState({ disabledFilters }, () => this.updateFilters({ types }));
  };

  handleRoomsFilterChange = (
    property: SearchFilters.RoomsFilterProperty,
    value: number,
  ) => {
    this.updateFilters({
      rooms: {
        ...this.state.rooms,
        [property]: value,
      },
    });
  };

  handleFurnitureFilterChange = (furniture: SearchFilters.FurnitureValue) => {
    this.updateFilters({ furniture });
  };

  handleBillsFilterChange = (bills: SearchFilters.BillsValue) => {
    this.updateFilters({ bills });
  };

  handleRegistrationFilterChange = (
    registration: SearchFilters.RegistrationValue,
  ) => {
    this.updateFilters({ registration });
  };

  handleRulesFilterToggle = (
    selection: SearchFilters.RulesValue,
    selected: boolean,
  ) => {
    if (selected) {
      this.updateFilters({ rules: this.state.rules.concat(selection) });
    } else {
      this.updateFilters({
        rules: this.state.rules.filter(current => selection !== current),
      });
    }
  };

  handleGenderFilterToggle = (
    selection: SearchFilters.GenderValue,
    selected: boolean,
  ) => {
    if (selected) {
      this.updateFilters({ gender: this.state.gender.concat(selection) });
    } else {
      this.updateFilters({
        gender: this.state.gender.filter(current => selection !== current),
      });
    }
  };

  handleRecentlyAddedFilterChange = (
    value: SearchFilters.RecentlyAddedFilterValue,
  ) => {
    this.updateFilters({
      recentlyAdded: value,
    });
  };

  handleSuitableForFilterToggle = (
    selection: SearchFilters.SuitableForValue,
    selected: boolean,
  ) => {
    if (selected) {
      this.updateFilters({
        suitableFor: this.state.suitableFor.concat(selection),
      });
    } else {
      this.updateFilters({
        suitableFor: this.state.suitableFor.filter(
          current => selection !== current,
        ),
      });
    }
  };

  render() {
    const {
      countryCode,
      intl: { T },
      button,
      toggleFilterPopover,
      updateButtonText,
    } = this.props;
    const {
      types,
      furniture,
      bills,
      rooms,
      registration,
      recentlyAdded,
      rules,
      gender,
      suitableFor,
      disabledFilters,
    } = this.state;

    return (
      <FilterPopover
        button={button}
        updateButtonText={updateButtonText}
        resetButtonText={T('Reset filters')}
        onUpdateButtonClick={this.handleUpdate}
        onResetButtonClick={this.handleReset}
        onOpen={this.handleOpen}
        onClose={this.handleClose}
        toggleFilterPopover={toggleFilterPopover}
        width={WidthType.auto}
      >
        <TypeFilter types={types} onToggle={this.handleTypeFilterToggle} />

        <hr />

        <RoomsFilter
          rooms={rooms}
          onChange={this.handleRoomsFilterChange}
          isDisabled={disabledFilters.includes('rooms')}
        />

        <hr />

        <FurnitureFilter
          furniture={furniture}
          onChange={this.handleFurnitureFilterChange}
        />

        <hr />

        <BillsFilter bills={bills} onChange={this.handleBillsFilterChange} />

        <hr />

        <SuitableForFilter
          suitableFor={suitableFor}
          onToggle={this.handleSuitableForFilterToggle}
        />

        <hr />

        <GenderFilter
          gender={gender}
          onToggle={this.handleGenderFilterToggle}
        />

        <hr />

        <RulesFilter rules={rules} onToggle={this.handleRulesFilterToggle} />

        {RegistrationCountryCodes.includes(countryCode) && (
          <React.Fragment>
            <hr />

            <RegistrationFilter
              registration={registration}
              onChange={this.handleRegistrationFilterChange}
            />
          </React.Fragment>
        )}

        <hr />

        <RecentlyAddedFilter
          value={recentlyAdded}
          onChange={this.handleRecentlyAddedFilterChange}
        />
      </FilterPopover>
    );
  }
}

export default withIntl(OtherFilterDesktop);
